// sanitizeHtml: defense-in-depth wrapper around DOMPurify.
// Treats every input as untrusted (see react-rewrite-plan §8.4).
// Returns a string of HTML safe to inject via dangerouslySetInnerHTML.

import DOMPurify from 'dompurify';
import { SAFE_ATTRS, SAFE_SVG_ATTRS, SAFE_SVG_TAGS, SAFE_TAGS } from './allowlist';

// Schemes that must never appear in href. data: is included because
// data:text/html can carry script payloads.
const UNSAFE_HREF_SCHEMES = ['javascript:', 'data:', 'vbscript:'];

function hasUnsafeScheme(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return UNSAFE_HREF_SCHEMES.some((scheme) => normalized.startsWith(scheme));
}

// Belt-and-suspenders: after DOMPurify, walk the resulting fragment and
// drop any href whose scheme is dangerous. DOMPurify already blocks
// javascript: by default, but we re-check to guard against config drift
// and to cover data:/vbscript: explicitly.
function stripUnsafeHrefs(root: DocumentFragment): void {
  const anchors = root.querySelectorAll('a[href]');
  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href');
    if (href !== null && hasUnsafeScheme(href)) {
      anchor.removeAttribute('href');
    }
  });
}

// Force safe rel behavior on external links, matching
// MarkdownComponents.tsx's MarkdownLink — regardless of what the source
// HTML specified, target="_blank" always gets rel="noopener noreferrer"
// (defends against reverse tabnabbing).
function forceSafeRel(root: DocumentFragment): void {
  root.querySelectorAll('a[target="_blank"]').forEach((anchor) => {
    anchor.setAttribute('rel', 'noopener noreferrer');
  });
}

// img[src] has no scheme restriction from DOMPurify itself — only http(s)
// is allowed (matches SafeImage in MarkdownComponents.tsx); anything else
// (data:, blob:, javascript:) gets the element dropped entirely, and a
// lazy-loading hint is added.
function sanitizeImages(root: DocumentFragment): void {
  root.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src');
    if (src === null || !/^https?:\/\//i.test(src.trim())) {
      img.remove();
      return;
    }
    img.setAttribute('loading', 'lazy');
  });
}

export function sanitizeHtml(input: string): string {
  if (typeof input !== 'string' || input.length === 0) return '';
  const purified = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [...SAFE_TAGS],
    ALLOWED_ATTR: [...SAFE_ATTRS],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: [
      'onerror',
      'onload',
      'onclick',
      'onmouseover',
      'onfocus',
    ],
  });
  if (typeof document === 'undefined') return purified;
  const template = document.createElement('template');
  template.innerHTML = purified;
  stripUnsafeHrefs(template.content);
  forceSafeRel(template.content);
  sanitizeImages(template.content);
  return template.innerHTML;
}

// Sibling sanitizer for raw agent-authored SVG markup (Svg.tsx). Uses
// DOMPurify's built-in SVG profile, further restricted to SAFE_SVG_TAGS/ATTRS
// — no <script>/<foreignObject>/event-handler attributes reach the DOM.
export function sanitizeSvg(input: string): string {
  if (typeof input !== 'string' || input.length === 0) return '';
  return DOMPurify.sanitize(input, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ALLOWED_TAGS: [...SAFE_SVG_TAGS],
    ALLOWED_ATTR: [...SAFE_SVG_ATTRS],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'foreignObject', 'style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
  });
}
